/**
 * Extracts a user-facing error message from a service result-object error shape
 * (e.g. `{ code?: string; messages?: string[] }`).
 *
 * Priority:
 * 1. `code` present → try `t('<i18nErrorPrefix>.<code>')` (code lowercased).
 *    If the key resolves (translation differs from the key), return the translation.
 * 2. `messages` array present and non-empty → join with newline.
 * 3. Otherwise → `undefined` (caller falls back to its own default toast).
 *
 * Example:
 *   extractErrorMessage(error, t, 'admin_invoice_reports.error');
 *   extractErrorMessage(error, t, 'invoice_reports.error');
 */
export function extractErrorMessage(
  error: unknown,
  t: (key: string) => string,
  i18nErrorPrefix: string
): string | undefined {
  if (error === null || typeof error !== 'object') return undefined;

  if ('code' in error && typeof error.code === 'string') {
    const key = `${i18nErrorPrefix}.${error.code.toLowerCase()}`;
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }
  }

  if (
    'messages' in error &&
    Array.isArray(error.messages) &&
    error.messages.length > 0
  ) {
    return error.messages.join('\n');
  }

  return undefined;
}
